import React, { useState, useRef, useEffect } from 'react';

const AIAssistant = () => {
  const [messages, setMessages] = useState([
    { role: 'system', content: 'Hello! I\'m Kiwi AI, your personal assistant. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);

  // Groq API configuration
  const GROQ_API_KEY = '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';
  const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
  const MODEL = 'llama3-70b-8192';
  
  // Auto-scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const callGroqAPI = async (messageHistory) => {
    try {
      // Add system instruction to ensure AI refers to itself as Kiwi AI
      const messagesWithInstructions = [
        { 
          role: 'system', 
          content: 'You are Kiwi AI, a helpful assistant. Always refer to yourself as "Kiwi AI" and never mention any other model names or underlying technology.' 
        },
        ...messageHistory
      ];
      
      const response = await fetch(GROQ_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${GROQ_API_KEY}`
        },
        body: JSON.stringify({
          model: MODEL,
          messages: messagesWithInstructions,
          temperature: 0.7,
          max_tokens: 1024
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
      }
      
      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error calling API:', error);
      return `I'm sorry, I encountered an error while processing your request: ${error.message}`;
    }
  };

  const sendMessage = async () => {
    if (input.trim() === '' || isProcessing) return;

    // Add user message to chat
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);
    
    // Prepare message history for API call (excluding system UI messages)
    const messageHistory = messages
      .filter(msg => msg.role === 'user' || msg.role === 'assistant')
      .concat(userMessage);
    
    try {
      // Call the API
      const aiResponse = await callGroqAPI(messageHistory);
      
      // Add AI response to chat
      const assistantMessage = { role: 'assistant', content: aiResponse };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      // Add error message if API call fails
      const errorMessage = { 
        role: 'system', 
        content: `Error: Unable to get response from Kiwi AI. ${error.message}` 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      backgroundColor: 'rgba(10, 10, 20, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <div className="chat-header" style={{ 
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: 'rgba(20, 20, 40, 0.3)',
        backdropFilter: 'blur(15px)',
        color: 'white',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center'
      }}>
        <i className="fas fa-robot" style={{ marginRight: '8px', color: '#4a90e2', textShadow: '0 0 5px rgba(74, 144, 226, 0.5)' }}></i>
        Kiwi AI
      </div>
      
      <div className="messages-container" style={{ 
        flexGrow: 1, 
        overflow: 'auto',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        backgroundColor: 'rgba(5, 5, 15, 0.2)',
        backdropFilter: 'blur(5px)'
      }}>
        {messages.map((message, index) => (
          <div 
            key={index}
            className={`message ${message.role === 'user' ? 'user-message' : 'system-message'}`}
            style={{
              maxWidth: '75%',
              padding: '10px 14px',
              borderRadius: '16px',
              alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
              backgroundColor: message.role === 'user' ? 'rgba(74, 144, 226, 0.5)' : 'rgba(40, 40, 60, 0.3)',
              backdropFilter: message.role === 'user' ? 'none' : 'blur(5px)',
              color: message.role === 'user' ? 'white' : '#eee',
              wordBreak: 'break-word',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
              border: message.role === 'user' ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            {message.content}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="input-container" style={{ 
        padding: '12px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: 'rgba(20, 20, 40, 0.3)',
        backdropFilter: 'blur(15px)'
      }}>
        <textarea
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          style={{
            flexGrow: 1,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            padding: '10px 14px',
            outline: 'none',
            resize: 'none',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            minHeight: '42px',
            maxHeight: '120px',
            backgroundColor: 'rgba(30, 30, 50, 0.2)',
            backdropFilter: 'blur(5px)',
            color: '#fff'
          }}
          rows={1}
        />
        <button 
          onClick={sendMessage}
          disabled={isProcessing || input.trim() === ''}
          style={{
            backgroundColor: 'rgba(74, 144, 226, 0.6)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '42px',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isProcessing || input.trim() === '' ? 'not-allowed' : 'pointer',
            opacity: isProcessing || input.trim() === '' ? 0.7 : 1,
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
          }}
        >
          <i className={isProcessing ? "fas fa-circle-notch fa-spin" : "fas fa-paper-plane"}></i>
        </button>
      </div>
    </div>
  );
};

export default AIAssistant; 